import React, { useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import { YMaps, Map, Clusterer, Placemark, Polyline } from "react-yandex-maps";
import ReactDOM from "react-dom";
import ReactDOMServer from "react-dom/server";
import styles from "./index.module.scss";
import Balloon from "../balloon";

const BalloonContentLayout = (layoutFactory, Component) => {
  const html = ReactDOMServer.renderToString(Component);
  const Layout = layoutFactory.createClass(`<div id="balloon">${html}</div>`, {
    build() {
      Layout.superclass.build.call(this);
    },
  });

  return Layout;
};

function mapPointLat(p) {
  return p.gpsLat ?? p.GPS_N;
}
function mapPointLon(p) {
  return p.gpsLon ?? p.GPS_S;
}
function mapPointId(p) {
  return p.id ?? p.ID;
}
function mapPointScheme(p) {
  return p.directionsScheme ?? p.DRIVE_SCHEME;
}

export default function Maper({
  points,
  pointId,
  zoom,
  height = 500,
  fluidHeight = false,
}) {
  const [ymaps, setYmaps] = useState(null);
  const [mapRef, setMapRef] = useState(null);

  const onPlacemarkClick = () => () => {};

  const pointCoords = useMemo(() => {
    return points
      .map((point) => {
        const gpsN = mapPointLat(point);
        const gpsS = mapPointLon(point);
        return gpsN != null && gpsS != null ? [gpsN, gpsS] : null;
      })
      .filter(Boolean);
  }, [points]);

  useEffect(() => {
    if (ymaps && mapRef && pointCoords.length > 0) {
      mapRef.setBounds(ymaps.util.bounds.fromPoints(pointCoords));
      mapRef.setZoom(zoom);
    }
  }, [ymaps, mapRef, pointCoords]);

  useEffect(() => {
    if (ymaps && mapRef && pointId) {
      const point = points.find((p) => mapPointId(p) === pointId);
      if (point) {
        const gpsN = mapPointLat(point);
        const gpsS = mapPointLon(point);
        if (gpsN != null && gpsS != null) mapRef.setCenter([gpsN, gpsS]);
      }
      mapRef.setZoom(16);
    }
  }, [pointId, ymaps, mapRef, points]);

  return (
    <div className={clsx(styles.Map, fluidHeight && styles.MapFluid)}>
      <YMaps query={{ lang: "ru_RU", load: "package.full" }}>
        <Map
          onLoad={(ymaps) => setYmaps(ymaps)}
          className={styles.MapBlock}
          style={{
            height,
          }}
          defaultState={{ center: [0, 0], zoom: 9 }}
          instanceRef={(ref) => {
            if (ref) {
              setMapRef(ref);
            }
          }}
        >
          {ymaps &&
            points.map((point) => {
              const scheme = mapPointScheme(point);
              const key = mapPointId(point);
              return (
                scheme && (
                  <Polyline
                    key={key}
                    geometry={scheme}
                    options={{
                      balloonCloseButton: false,
                      strokeColor: "#1247a5",
                      strokeWidth: 4,
                      strokeOpacity: 0.5,
                    }}
                  />
                )
              );
            })}
          <Clusterer
            options={{
              preset: "islands#invertedVioletClusterIcons",
            }}
          >
            {ymaps &&
              points.map((point) => {
                const gpsN = mapPointLat(point);
                const gpsS = mapPointLon(point);
                const key = mapPointId(point);
                if (gpsN == null || gpsS == null) return null;
                return (
                  <Placemark
                    modules={[
                      "geoObject.addon.balloon",
                      "geoObject.addon.hint",
                    ]}
                    key={key}
                    geometry={[gpsN, gpsS]}
                    onClick={onPlacemarkClick(point)}
                    onBalloonOpen={() => {
                      ReactDOM.hydrate(
                        <Balloon point={point} pointId={pointId} />,
                        document.getElementById("balloon"),
                      );
                    }}
                    options={{
                      balloonContentLayout: BalloonContentLayout(
                        ymaps.templateLayoutFactory,
                        <Balloon point={point} pointId={pointId} />,
                      ),
                    }}
                  />
                );
              })}
          </Clusterer>
        </Map>
      </YMaps>
      <div className={styles.Link}>
        <div>© Яндекс</div>
        <div>
          &nbsp;
          <a
            href="https://yandex.ru/legal/maps_termsofuse/ru/?lang=ru"
            target="_blank"
            rel="noopener noreferrer"
          >
            Условия использования
          </a>
        </div>
      </div>
    </div>
  );
}
